import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { Input, Offcanvas, OffcanvasBody, OffcanvasHeader } from 'reactstrap';
import classNames from 'classnames';
import { v4 as uuidv4 } from 'uuid';

import { AuthTags } from '~constants/authTags';

import { formatCurrency, getChangedValues } from '~libs/dataUtils';

import { Button, ConfirmDialog, ErrorResponse, Spinner, TabContainer } from '~components';
import { DateInput, Select, Switch } from '~components/form-fields/inputs';
import { Tab } from '~components/TabContainer';
import { Row, TableView, TableViewHandle } from '~components/table-view';

import { InvoiceStatus, InvoiceStatusTransitions } from '~lookups/models';
import {
  AnalyseInvoiceResponse,
  SupplierInvoiceItem,
  SupplierInvoice as SupplierInvoiceType,
} from '~support-at-home/models';

import {
  deriveTotals,
  fetchValidServices,
  refreshItemMeta,
  supplierInvoiceColumnsFactory,
} from '~support-at-home/forms/supplier-invoices/supplierInvoiceItems';

import { getAll } from '~appointments/actions/serviceTypes';
import { getAll as getAllStatuses, useGetInvoiceStatusTransitionsQuery } from '~lookups/actions/invoiceStatuses';
import { getAll as getAllClients } from '~main/actions/clients';
import { getClientPackageSchedules } from '~main/actions/clients/clientPackageSchedules';
import { useGetAccountCodesQuery } from '~support-at-home/actions/accountCodes';
import { useLazyGetAssocByServiceTypeQuery } from '~support-at-home/actions/sahServiceTypeAssociations';
import { createInvoice, updateInvoice } from '~support-at-home/actions/supplierInvoices';
import { getAll as getAllSuppliers } from '~support-at-home/actions/suppliers';

import { clearClientPackageSchedules } from '~main/reducers/clients/clientPackageSchedules';

import { selectAppointmentServiceTypes } from '~appointments/selectors/serviceTypes';
import { selectInvoiceStatuses } from '~lookups/selectors/invoiceStatuses';
import { selectClientPackageSchedules } from '~main/selectors/clients/clientPackageSchedules';
import { selectLookupClients } from '~main/selectors/clients/clients';
import { selectUserTags } from '~main/selectors/login';
import { selectAllErrors, selectCurrentSupplierInvoice } from '~support-at-home/selectors/supplierInvoices';
import { selectSuppliers } from '~support-at-home/selectors/suppliers';

import SupplierInvoiceActivityLog from './SupplierInvoiceActivityLog';

interface OcrHints {
  supplier_name: string | null;
  client_name: string | null;
}

interface ComponentProps {
  isOpen: boolean;
  isNew: boolean;
  onClose: (confirm: boolean, id?: string) => void;
  className?: string;
  initialValues?: Partial<SupplierInvoiceType>;
  authTag?: string;
  ocrResult?: AnalyseInvoiceResponse | null;
}

/**
 * Find the best fuzzy match for an OCR name against a list of options.
 * Checks: exact match, case-insensitive match, one name contains the other.
 * Returns the matched option's id or null.
 */
const MIN_FUZZY_LENGTH = 3;

function fuzzyMatchOption(ocrName: string | null, options: Array<{ id: string | number; name: string }>) {
  if (!ocrName || options.length === 0) return null;

  const needle = ocrName.toLowerCase().trim();
  if (needle.length < MIN_FUZZY_LENGTH) return null;

  // Exact case-insensitive match
  const exact = options.find((opt) => opt.name.toLowerCase().trim() === needle);
  if (exact) return exact.id;

  // OCR name contains option name, or option name contains OCR name
  // Both strings must meet minimum length to avoid false positives on short substrings
  const partial = options.find((opt) => {
    const optName = opt.name.toLowerCase().trim();
    if (optName.length < MIN_FUZZY_LENGTH) return false;
    return needle.includes(optName) || optName.includes(needle);
  });

  return partial?.id ?? null;
}

const SupplierInvoice: React.FC<ComponentProps> = ({ isOpen, onClose, isNew, className, ocrResult }) => {
  const dispatch = useAppDispatch();
  const error = useAppSelector(selectAllErrors);
  const { data: accountCodes = [] } = useGetAccountCodesQuery();
  const supplierInvoice = useAppSelector(selectCurrentSupplierInvoice);
  const packageSchedules = useAppSelector(selectClientPackageSchedules);
  const serviceTypes = useAppSelector(selectAppointmentServiceTypes);
  const invoiceStatuses = useAppSelector(selectInvoiceStatuses);
  const suppliers = useAppSelector(selectSuppliers);
  const clients = useAppSelector(selectLookupClients);

  const authTags = AuthTags.SUPPORTATHOME.SUPPLIERINVOICES;
  const authTagUpdate = authTags.UPDATE;

  const tags = useAppSelector(selectUserTags);
  const authorised = tags.includes(authTagUpdate);
  const hasOverrideAccess = tags.includes(authTags.ADMIN) || tags.includes(authTags.OVERRIDE);

  const initialFormData = useMemo(
    (): Partial<SupplierInvoiceType> => ({
      client_id: undefined,
      supplier_id: '',
      status_id: invoiceStatuses.find((status: InvoiceStatus) => status.tag === 'pending')?.id,
      invoice_date: '',
      due_date: '',
      reference: '',
      items: [],
    }),
    [invoiceStatuses],
  );

  const defaultItemData = useMemo(
    () => ({
      gst_type: 'nin',
    }),
    [],
  );

  const [formData, setFormData] = useState(initialFormData);
  const [originalValues, setOriginalValues] = useState(initialFormData);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [isLoading, setLoading] = useState<boolean>(false);
  const [searchByPackage, setSearchByPackage] = useState<boolean>(true);
  const [includeDeletedClients, setIncludeDeletedClients] = useState<boolean>(false);
  const [ocrHints, setOcrHints] = useState<OcrHints>({ supplier_name: null, client_name: null });
  const [pendingChange, setPendingChange] = useState<{
    newClientId: number;
    previousClientId: number | undefined;
  } | null>(null);
  const [showClientChangeConfirm, setShowClientChangeConfirm] = useState(false);

  const [fetchAssocByServiceType] = useLazyGetAssocByServiceTypeQuery();

  useEffect(() => {
    dispatch(getAll());
    dispatch(getAllSuppliers({ limit: null }));
    dispatch(getAllStatuses());
  }, []);

  // Apply OCR results to form — only fills empty fields.
  // Deps are intentionally [ocrResult] only: suppliers/clients are read via refs so that
  // toggling "include deleted clients" doesn't regenerate OCR rows and discard user edits.
  const suppliersRef = useRef(suppliers);
  suppliersRef.current = suppliers;
  const clientsRef = useRef(clients);
  clientsRef.current = clients;

  useEffect(() => {
    if (!ocrResult) {
      setOcrHints({ supplier_name: null, client_name: null });
      return;
    }

    const matchedSupplierId = fuzzyMatchOption(ocrResult.invoice.supplier_name, suppliersRef.current);
    const matchedClientId = fuzzyMatchOption(ocrResult.invoice.client_name, clientsRef.current);

    const ocrItems = ocrResult.items.map(
      (item) =>
        ({
          id: uuidv4(),
          description: item.description || '',
          service_date: item.service_date || '',
          units: item.units ?? undefined,
          unit_amount: item.unit_amount ?? undefined,
          gst_type: item.gst_type,
          _isOcr: true,
        }) as SupplierInvoiceItem,
    );

    setFormData((prev) => {
      // Remove previously OCR-appended items before adding fresh ones to prevent duplicates
      const manualItems = (prev.items || []).filter((item: any) => !item._isOcr);

      return {
        ...prev,
        reference: prev.reference || ocrResult.invoice.reference || '',
        invoice_date: prev.invoice_date || ocrResult.invoice.invoice_date || '',
        due_date: prev.due_date || ocrResult.invoice.due_date || '',
        supplier_id: prev.supplier_id || (matchedSupplierId != null ? String(matchedSupplierId) : ''),
        client_id: prev.client_id ?? (matchedClientId != null ? Number(matchedClientId) : undefined),
        items: [...manualItems, ...ocrItems],
      };
    });

    // Load package schedules when OCR auto-selects a client — same downstream fetch as manual selection
    if (matchedClientId) {
      dispatch(clearClientPackageSchedules());
      dispatch(getClientPackageSchedules({ client_id: Number(matchedClientId), sah: true, includeEnded: true }));
    }

    setOcrHints({
      supplier_name: matchedSupplierId ? null : ocrResult.invoice.supplier_name,
      client_name: matchedClientId ? null : ocrResult.invoice.client_name,
    });
  }, [ocrResult]);

  useEffect(() => {
    let cancelled = false;

    const preloadItemMeta = async (mode: 'package' | 'po') => {
      if (!supplierInvoice) return;

      const client_id = supplierInvoice.client_id ? Number(supplierInvoice.client_id) : undefined;

      const itemsWithMeta = await Promise.all(
        (supplierInvoice.items ?? []).map(async (item) => {
          const { client_budget_id, _meta } = await refreshItemMeta(item as any, client_id, mode);
          return { ...item, client_budget_id, _meta } as SupplierInvoiceItem;
        }),
      );

      if (cancelled) return;

      const loadedData: Partial<SupplierInvoiceType> = {
        id: supplierInvoice.id,
        client_id,
        supplier_id: supplierInvoice.supplier_id || '',
        status_id: supplierInvoice.status_id || '',
        status: supplierInvoice.status,
        invoice_date: supplierInvoice.invoice_date || '',
        due_date: supplierInvoice.due_date || '',
        reference: supplierInvoice.reference || '',
        items: itemsWithMeta,
      };

      setFormData(loadedData);
      setOriginalValues(loadedData);
      dispatch(getClientPackageSchedules({ client_id: loadedData.client_id, sah: true, includeEnded: true }));
    };

    if (supplierInvoice) {
      const hasPurchaseOrders = (supplierInvoice.items ?? []).some((item) => item.purchase_order_number);
      const mode = hasPurchaseOrders ? 'po' : 'package';
      setSearchByPackage(!hasPurchaseOrders);
      preloadItemMeta(mode);
    } else if (isNew) {
      setSearchByPackage(true);
      setFormData(initialFormData);
      setOriginalValues(initialFormData);
    }

    return () => { cancelled = true; };
  }, [supplierInvoice, isNew, initialFormData]);

  const getErrorKey = (fieldName: string): string => {
    const errorKeyMap: Record<string, string> = {
      client_id: 'client',
      supplier_id: 'supplier',
      status_id: 'status',
    };
    return errorKeyMap[fieldName] || fieldName;
  };

  const validateField = useCallback((name: string, value: any): string | null => {
    switch (name) {
      case 'client_id':
        return !value ? 'Client is required' : null;
      case 'supplier_id':
        return !value ? 'Supplier is required' : null;
      case 'reference':
        return !value || !value.trim() ? 'Invoice number is required' : null;
      case 'invoice_date':
        return !value ? 'Invoice date is required' : null;
      default:
        return null;
    }
  }, []);

  const onFormChange = useCallback(
    (name: string, value: any) => {
      // Update form data using functional update to avoid dependencies
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));

      // Validate the field and update errors accordingly using functional update
      setValidationErrors((prev) => {
        const fieldError = validateField(name, value);
        const errorKey = getErrorKey(name);
        const newErrors = { ...prev };

        if (fieldError) {
          newErrors[errorKey] = fieldError;
        } else {
          delete newErrors[errorKey];
        }

        return newErrors;
      });
    },
    [validateField],
  );

  const { data: allowedStatusTransitions = [] } = useGetInvoiceStatusTransitionsQuery();

  const getStatusTransitionOptions = () => {
    if (isNew) return [{ label: 'Pending', value: initialFormData.status_id as string }];

    if (hasOverrideAccess) {
      // Admin/Override users can select any valid status
      return invoiceStatuses.map((status: InvoiceStatus) => ({
        label: status.name,
        value: status.id,
      }));
    }

    const mappedTransitions = allowedStatusTransitions
      .filter((transition: InvoiceStatusTransitions) => transition.from_status_id === originalValues.status_id)
      .map((transition: InvoiceStatusTransitions) => ({
        label: transition.to_status?.name,
        value: transition.to_status_id,
      }))
      .filter((obj, ii, self) => self.findIndex((tt) => tt.value === obj.value) === ii);

    return [
      ...mappedTransitions,
      { label: originalValues.status?.name, value: originalValues.status_id } as { label: string; value: string },
    ];
  };

  const renderButtonContent = () => {
    if (isLoading) {
      return <Spinner loading={isLoading} size={4} />;
    }
    return isNew ? 'Create Invoice' : 'Update Invoice';
  };

  const handleClientUpdate = useCallback(
    (client_id: number, previousClientId: number | undefined) => {
      setFormData((prev) => {
        // Preserve OCR-populated items when selecting a client for the first time
        const shouldClearItems = !!previousClientId;
        return {
          ...prev,
          items: shouldClearItems ? [] : prev.items,
        };
      });
      dispatch(clearClientPackageSchedules());

      // Refetch package schedules for new client
      if (client_id) {
        dispatch(getClientPackageSchedules({ client_id, sah: true, includeEnded: true }));
      }
    },
    [dispatch],
  );

  const handleClientSelect = useCallback(
    (field: string, value: any) => {
      const previousClientId = formData.client_id;
      const hasItems = (formData.items?.length ?? 0) > 0;

      // Always update the field value immediately for UI responsiveness
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));

      // Validate client field
      setValidationErrors((prev) => {
        const fieldError = validateField(field, value);
        const errorKey = getErrorKey(field);
        const newErrors = { ...prev };
        if (fieldError) newErrors[errorKey] = fieldError;
        else delete newErrors[errorKey];
        return newErrors;
      });

      if (hasItems && !!previousClientId) {
        // Items exist and client is changing — gate with confirmation dialog
        setPendingChange({ newClientId: value, previousClientId });
        setShowClientChangeConfirm(true);
      } else {
        // No items, or first-time client selection — proceed immediately
        handleClientUpdate(value, previousClientId);
      }
    },
    [validateField, handleClientUpdate, formData.client_id, formData.items],
  );

  const handleClientChangeConfirm = useCallback(
    (confirmed: boolean) => {
      setShowClientChangeConfirm(false);
      if (confirmed && pendingChange !== null) {
        handleClientUpdate(pendingChange.newClientId, pendingChange.previousClientId);
      } else if (!confirmed && pendingChange !== null) {
        // Revert to previous client
        setFormData((prev) => ({
          ...prev,
          client_id: pendingChange.previousClientId,
        }));
        // Revert the optimistic validation update applied in handleClientSelect.
        // If the user selected the empty (null) option, validateField set a
        // "Client is required" error; re-validate against the reverted value so a
        // spurious error doesn't linger on the field.
        setValidationErrors((prev) => {
          const fieldError = validateField('client_id', pendingChange.previousClientId);
          const errorKey = getErrorKey('client_id');
          const newErrors = { ...prev };
          if (fieldError) newErrors[errorKey] = fieldError;
          else delete newErrors[errorKey];
          return newErrors;
        });
      }
      setPendingChange(null);
    },
    [pendingChange, handleClientUpdate, validateField],
  );

  const clientAttributesWithDisabled = useMemo(
    () => [
      'id',
      'full_name',
      'default_package_id',
      'non_chargeable',
      'address',
      'preferences',
      'description',
      'disabled',
    ],
    [],
  );

  const handleDeletedClientsToggle = useCallback(
    (_field: string, value: boolean) => {
      setIncludeDeletedClients(value);
      if (value) {
        dispatch(getAllClients({ disabled: undefined, attributes: clientAttributesWithDisabled }));
      } else {
        dispatch(getAllClients({}));
      }
    },
    [dispatch, clientAttributesWithDisabled],
  );

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Validate all fields using the same validation logic
    const fieldsToValidate = ['client_id', 'supplier_id', 'reference', 'invoice_date', 'status_id'];

    fieldsToValidate.forEach((fieldName) => {
      const fieldError = validateField(fieldName, (formData as any)[fieldName]);
      if (fieldError) {
        const errorKey = getErrorKey(fieldName);
        errors[errorKey] = fieldError;
      }
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const cleanPayloadForSubmission = useCallback(
    (data: Partial<SupplierInvoiceType>) => {
      let payload: any = {};
      if (isNew) {
        // For new records, filter out empty/initial values
        Object.entries(data).forEach(([key, value]) => {
          // Skip empty strings, undefined values, and empty arrays
          if (value !== '' && value !== undefined && !(Array.isArray(value) && value.length === 0)) {
            // For string fields, also skip whitespace-only values
            if (typeof value === 'string' && !value.trim()) {
              return;
            }
            payload[key] = value;
          }
        });
      } else {
        // For existing records, only send changed values
        payload = getChangedValues(data, originalValues);
      }

      // always retain id for updates
      if (data.id) {
        payload.id = data.id;
      }

      // strip _meta fields, nested objects, client-only markers, and read-only claim info from items
      const items = (payload.items ?? data.items ?? []).map(
        ({
          _isOcr,
          reference,
          sah_wrap,
          sah_item,
          budget,
          total,
          service_details,
          _meta,
          sah_claim_id,
          sah_reconciliation_id,
          is_claimed,
          is_deleted,
          ...rest
        }: SupplierInvoiceItem & {
          _isOcr?: boolean;
          sah_claim_id?: string | null;
          sah_reconciliation_id?: string | null;
          is_claimed?: boolean | null;
          is_deleted?: boolean | null;
        }) => ({
          ...defaultItemData,
          ...rest,
        }),
      );

      return { ...payload, items };
    },
    [isNew, getChangedValues, originalValues],
  );

  const handleSubmit = async () => {
    const itemsValid = await tableRef.current?.validate();
    if (!validateForm() || !itemsValid) {
      return;
    }

    setLoading(true);

    const cleanPayload = cleanPayloadForSubmission(formData);

    try {
      let result;
      if (isNew) {
        result = await dispatch(createInvoice(cleanPayload));
      } else {
        result = await dispatch(updateInvoice(cleanPayload));
      }

      if (result.payload?.success) {
        if (includeDeletedClients) {
          dispatch(getAllClients({}));
          setIncludeDeletedClients(false);
        }
        onClose(true, result.payload.data?.id);
      }
    } catch (err) {
      console.error('Error saving invoice:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = useCallback(() => {
    if (includeDeletedClients) {
      dispatch(getAllClients({}));
      setIncludeDeletedClients(false);
    }
    setFormData(initialFormData);
    setOriginalValues(initialFormData);
    setValidationErrors({});
    onClose(false);
  }, [initialFormData, onClose, includeDeletedClients, dispatch]);

  const tableRef = useRef<TableViewHandle>(null);

  const supplierInvoiceItemsColumns = useMemo(() => {
    return supplierInvoiceColumnsFactory(
      searchByPackage ? 'package' : 'po',
      accountCodes,
      packageSchedules,
      serviceTypes,
      formData.client_id,
      setFormData,
      formData.invoice_date,
    );
  }, [searchByPackage, accountCodes, packageSchedules, serviceTypes, formData.client_id, formData.invoice_date]);

  const aggregateTotals = useMemo(() => {
    const items = formData.items ?? [];
    return items.reduce(
      (acc, item) => {
        const { net, gst, gross } = deriveTotals(item);
        return { net: acc.net + net, gst: acc.gst + gst, gross: acc.gross + gross };
      },
      { net: 0, gst: 0, gross: 0 },
    );
  }, [formData.items]);

  // Returns all rows on any table row change
  const handleDataChange = useCallback(
    (allRows: Row[]) => {
      onFormChange('items', allRows);
    },
    [onFormChange],
  );

  const handleRowEdit = async (row: any, isEditing: boolean) => {
    if (!isEditing) return; // only act on entering edit mode
    if (!formData.client_id || !row.service_date) return;
    if (row._meta && row._meta?.validServices) return;

    const extra = await fetchValidServices(row, {
      client_id: formData.client_id,
      mode: searchByPackage ? 'package' : 'po',
    });

    if (extra && extra._meta) {
      // merge into items to make _meta.validServices available to columnDefinitions
      setFormData((prev) => ({
        ...prev,
        items: prev.items?.map((item) => (item.id === row.id ? { ...item, _meta: extra._meta } : item)) || [],
      }));
    }

    if (row.service_type_id) {
      try {
        const { data: assoc } = await fetchAssocByServiceType({ id: row.service_type_id.toString() });
        if (assoc) {
          setFormData((prev) => ({
            ...prev,
            items:
              prev.items?.map((item) =>
                item.id === row.id
                  ? {
                      ...item,
                      _meta: { ...item._meta, assocItems: assoc.items, assocWraps: assoc.wraps },
                    }
                  : item,
              ) ?? [],
          }));
        }
      } catch (err) {
        console.error('Failed to fetch service associations', err);
      }
    }
  };

  const canvasClassName = classNames('supplier-invoice', [className]);
  const getValidationClassName = (check: string) => (check?.length > 0 ? 'border border-danger rounded' : '');

  const showActivityTab = !isNew && hasOverrideAccess;

  const invoiceFormContent = (
    <>
      <ErrorResponse message={error} />
      <div className="row d-flex justify-content-between">
        <div className="col top-pill">
          <label>Client</label>
          <Select
            id="client_id"
            name="client_id"
            type="select"
            className={getValidationClassName(validationErrors.client)}
            disabled={!authorised || !isNew}
            selectorOptions={selectLookupClients}
            value={formData.client_id}
            onChange={handleClientSelect}
          />
          {validationErrors.client && <div className="text-danger small">{validationErrors.client}</div>}
          {!formData.client_id && ocrHints.client_name && (
            <div className="text-info small">Detected: {ocrHints.client_name}</div>
          )}
          <Switch
            type="switch"
            id="include-deleted-clients"
            name="include-deleted-clients"
            value={includeDeletedClients}
            disabled={!authorised || !isNew}
            onChange={handleDeletedClientsToggle}
            labels={['Active clients only', 'Including deleted clients']}
          />
        </div>
        <div className="col top-pill">
          <label>Invoice Number</label>
          <Input
            type="text"
            id="reference"
            name="reference"
            className={getValidationClassName(validationErrors.reference)}
            value={formData.reference}
            onChange={(e) => onFormChange(e.target.name, e.target.value)}
            disabled={!authorised}
          />
          {validationErrors.reference && <div className="text-danger small">{validationErrors.reference}</div>}
        </div>
        <div className="col top-pill">
          <label>Supplier</label>
          <Select
            id="supplier_id"
            name="supplier_id"
            type="select"
            className={getValidationClassName(validationErrors.supplier)}
            selectorOptions={selectSuppliers}
            value={formData.supplier_id}
            onChange={onFormChange}
            disabled={!authorised}
          />
          {validationErrors.supplier && <div className="text-danger small">{validationErrors.supplier}</div>}
          {!formData.supplier_id && ocrHints.supplier_name && (
            <div className="text-info small">Detected: {ocrHints.supplier_name}</div>
          )}
        </div>
      </div>
      <div className="row d-flex justify-content-between">
        <div className="col top-pill">
          <label>Invoice Date</label>
          <DateInput
            type="date"
            id="invoice_date"
            name="invoice_date"
            className={getValidationClassName(validationErrors.invoice_date)}
            value={formData.invoice_date}
            onChange={onFormChange}
            disabled={!authorised}
          />
          {validationErrors.invoice_date && <div className="text-danger small">{validationErrors.invoice_date}</div>}
        </div>
        <div className="col top-pill">
          <label>Due Date</label>
          <DateInput
            type="date"
            id="due_date"
            name="due_date"
            value={formData.due_date}
            onChange={onFormChange}
            disabled={!authorised}
          />
        </div>
        <div className="col top-pill">
          <label>Status</label>
          <Select
            id="status_id"
            name="status_id"
            type="select"
            className={getValidationClassName(validationErrors.status)}
            options={invoiceStatuses}
            gateOptions={{ getValidOptions: getStatusTransitionOptions }}
            value={formData.status_id}
            onChange={onFormChange}
            disabled={!authorised || isNew}
          />
        </div>
      </div>
      <div className="my-1">
        <Switch
          type="switch"
          id="search-switch"
          name="search-switch"
          value={searchByPackage}
          disabled={!authorised || !isNew || (formData?.items?.length ?? 0) > 0}
          onChange={() => setSearchByPackage(!searchByPackage)}
          labels={['Find by PO Reference', 'Find by Package']}
        />
      </div>
      <TableView
        ref={tableRef}
        id="supplier-invoice-items-table"
        data={formData.items || []}
        columns={supplierInvoiceItemsColumns}
        onChange={handleDataChange}
        onEditRow={handleRowEdit}
        context={{ allRows: formData.items, client_id: formData.client_id, mode: searchByPackage ? 'package' : 'po' }}
        readonly={!authorised}
        compactView
        editonly
        bordered
      />
      {(formData.items?.length ?? 0) > 0 && (
        <div className="d-flex justify-content-end mt-2" data-cy="supplier-invoice-totals-summary">
          <table className="supplier-invoice-totals-table">
            <tbody>
              <tr>
                <td className="text-muted pe-4">Net</td>
                <td className="text-end fw-semibold" data-cy="supplier-invoice-totals-net">
                  {formatCurrency(aggregateTotals.net)}
                </td>
              </tr>
              <tr>
                <td className="text-muted pe-4">GST</td>
                <td className="text-end fw-semibold" data-cy="supplier-invoice-totals-gst">
                  {formatCurrency(aggregateTotals.gst)}
                </td>
              </tr>
              <tr className="border-top">
                <td className="pe-4 fw-bold">Gross</td>
                <td className="text-end fw-bold" data-cy="supplier-invoice-totals-gross">
                  {formatCurrency(aggregateTotals.gross)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
      <div className="d-flex justify-content-end">
        <Button
          onClick={handleSubmit}
          color="success"
          disabled={isLoading || (formData.items?.length ?? 0) === 0}
          authTag={authTagUpdate}
        >
          {renderButtonContent()}
        </Button>
      </div>
      <ConfirmDialog
        isOpen={showClientChangeConfirm}
        messages={[
          'Changing the client will clear all existing line items.',
          'This action cannot be undone.',
        ]}
        onClose={handleClientChangeConfirm}
      />
    </>
  );

  // Stabilise component identity for TabContainer to prevent unmount/remount of TableView,
  // which destroys validation state (red highlights). The ref holds the latest JSX while
  // useCallback keeps a stable component reference for React reconciliation.
  const invoiceContentRef = useRef(invoiceFormContent);
  invoiceContentRef.current = invoiceFormContent;

  const InvoiceTab = useCallback<React.FC>(() => <>{invoiceContentRef.current}</>, []);
  const ActivityTab = useCallback<React.FC>(
    () => <SupplierInvoiceActivityLog id={supplierInvoice?.id} />,
    [supplierInvoice?.id],
  );

  const tabs: Tab[] = useMemo(
    () => [
      { id: 'invoice', title: 'Invoice', component: InvoiceTab },
      ...(showActivityTab ? [{ id: 'activity', title: 'Activity', component: ActivityTab }] : []),
    ],
    [InvoiceTab, ActivityTab, showActivityTab],
  );

  return (
    <Offcanvas isOpen={isOpen} size="xl" className={canvasClassName} direction="end">
      <OffcanvasHeader className="header">{isNew ? 'New Supplier Invoice' : 'Edit Supplier Invoice'}</OffcanvasHeader>
      <OffcanvasBody>
        {showActivityTab ? <TabContainer tabs={tabs} setPageTitle={false} /> : invoiceFormContent}
        <div className="d-flex mt-2">
          <Button onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
        </div>
      </OffcanvasBody>
    </Offcanvas>
  );
};

export default SupplierInvoice;
